import React, { useState } from 'react'
import { isNil } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import {
  ORG_NEW_FILES_PAUSED_LABEL,
  orgNewFilesPaused,
} from '@/views/Organizations/components/OrgSyncPausedBanner'
import { ReviewFilesModal } from '@/views/Organizations/components/ReviewFilesModal'
import {
  REVIEW_STATUS,
  reviewStatusOf,
} from '@/views/Organizations/components/ReviewFilesModal.helpers'

const activeTooltip = (connection) => {
  if (!connection.active) {
    // Resuming brings changes back, but not new files, while the org is over its ceiling.
    return orgNewFilesPaused(connection)
      ? 'Paused. Resuming turns it back on: changes and deletions sync, but new files wait ' +
          `until this organization is back under its size limit (see ${ORG_NEW_FILES_PAUSED_LABEL}).`
      : 'Syncing is paused for this connection. Use Resume syncing below to start it again.'
  }
  // The connection syncs, but agent-gpt adds no new files while the org is over its ceiling.
  return orgNewFilesPaused(connection)
    ? 'Syncing is on for changes and deletions, but new files wait while this organization ' +
        `is at its size limit: see ${ORG_NEW_FILES_PAUSED_LABEL}.`
    : 'Syncing is on: the nightly sync ingests this connection’s content. Use Pause ' +
        'syncing below to stop it.'
}

/**
 * The detail pane's header line for a persisted knowledge-base connection:
 * Active/Inactive with an explanation of what that means, freshness, and the
 * erasure marker. While the connection is in review, its review status replaces
 * Active/Inactive, and a connection ready for review offers a Review button.
 * Pause/Resume syncing itself lives in the form footer.
 *
 * @param {Object} props.connection the persisted connection row
 * @param {Object} props.syncState undefined | null | the connection's ingest state; a failed
 *   scan row reads off it
 * @param {Function} [props.onReviewClosed] called, with no argument, when the review modal closes;
 *   any form whose connections can enter review must pass it so the list reloads
 */
export const ConnectionStatusRow = ({ connection, syncState, onReviewClosed }) => {
  const [reviewing, setReviewing] = useState(false)
  const reviewStatus = reviewStatusOf(connection, syncState)

  const handleReviewClosed = () => {
    setReviewing(false)
    if (!isNil(onReviewClosed)) {
      onReviewClosed()
    }
  }

  return (
    <>
      <div className="flex-align-center medium-gap mbs">
        {isNil(reviewStatus) ? (
          <Pill
            success={connection.active}
            critical={!connection.active}
            small
            tooltip={activeTooltip(connection)}
          >
            {connection.active ? 'Active' : 'Inactive'}
          </Pill>
        ) : (
          <Pill {...{ [reviewStatus.variant]: true }} small tooltip={reviewStatus.tooltip}>
            {reviewStatus.label}
          </Pill>
        )}
        {reviewStatus === REVIEW_STATUS.READY && (
          <Button small brand onClick={() => setReviewing(true)}>
            Review
          </Button>
        )}
        {orgNewFilesPaused(connection) && (
          <Pill
            warning
            small
            tooltip="This organization is at its size limit, so new files aren’t being added until it drops below. Changes and deletions still sync."
          >
            {ORG_NEW_FILES_PAUSED_LABEL}
          </Pill>
        )}
        {!isNil(connection.last_synced_at) && (
          <span className="field-help small-text">
            Last synced {new Date(connection.last_synced_at).toLocaleString()}
          </span>
        )}
        {/* Erased content leaves no trace in the fields above: a purged connection
        reads exactly like a merely paused one, and re-activating it silently
        re-ingests everything from scratch. */}
        {!isNil(connection.purged_at) && (
          <span className="field-help small-text">
            Documents erased {new Date(connection.purged_at).toLocaleString()}
          </span>
        )}
        {/* QCER-1075: the Playbooks column reads the SAVED allowlist, so from the
        moment a scope is saved it shows something retrieval is not enforcing
        yet — the ingested documents keep the old scope until a sync re-tags
        them. Saying so is the difference between the panel reporting an
        isolation and merely claiming one. */}
        {connection.pending_retag && (
          <Pill
            warning
            small
            tooltip="A playbook scope changed. The documents already ingested still carry the previous scope until the next sync re-tags them, which also clears this."
          >
            Re-scoping pending
          </Pill>
        )}
      </div>
      {reviewing && (
        <ReviewFilesModal
          connectionId={connection.id}
          onClose={handleReviewClosed}
          onSubmitted={onReviewClosed}
        />
      )}
    </>
  )
}
